import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import WebProfitSpinner from "../components/system/WebProfitSpinner";
import { acquisitionApi } from "../api/acquisition";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectsContext";
import {
  ACQUISITION_EXPERIENCE_VERSION,
  acquisitionCampaign,
  acquisitionDeviceMetadata,
  acquisitionEventId,
  acquisitionSessionId,
  acquisitionVisitorId,
  clearAcquisitionDraft,
  loadAcquisitionDraft,
  saveAcquisitionDraft,
} from "../utils/acquisitionFunnel";

// ACQUISITION V4 · Lead-first public onboarding.
// STATUS: STATIC_PASS · RUNTIME_PENDING.
// UX CONTRACT: ask only what a normal business owner can answer immediately;
// persist the commercial lead BEFORE asking for an account password.
// RESILIENCE: local draft first, API second. A tracking failure never blocks conversion.

const BRAND = {
  orange: "#F35A3A",
  dark: "#0B1016",
  panel: "#111820",
  muted: "rgba(255,255,255,.58)",
};

// Keep legacy analytics keys (need/business_identity/contact) so historical and
// V4 sessions remain comparable without creating parallel tracking contracts.
const STEPS = ["intro", "need", "business_identity", "contact"];
const PROFILE_KEYS = new Set(["commerce", "services", "gym", "restaurant", "real_estate"]);

// ACQUISITION V4.1 · Human-first intent choices.
// STATUS: STATIC_PASS · RUNTIME_PENDING.
// CONTRACT: this is guidance only. The selected option never restricts enabled modules.
const GUIDED_NEEDS = [
  { key: "website", title: "Tener una página web", helper: "Mostrá tu negocio, recibí consultas y empezá a vender desde una web editable.", icon: <LanguageRoundedIcon /> },
  { key: "stock", title: "Controlar stock y movimientos", helper: "Entradas, salidas y existencias claras sin depender de la memoria.", icon: <Inventory2RoundedIcon /> },
  { key: "memberships", title: "Controlar mensualidades", helper: "Ej.: gimnasio, academia, club o cualquier negocio que cobre cuotas periódicas.", icon: <PaymentsRoundedIcon /> },
  { key: "appointments", title: "Recibir turnos por una web", helper: "Ej.: estética, peluquería o servicios; reservas online y señas desde la misma experiencia.", icon: <EventAvailableRoundedIcon /> },
  { key: "property_payments", title: "Automatizar pagos de inmuebles", helper: "Alquileres, vencimientos y seguimiento de pagos desde un solo lugar.", icon: <ApartmentRoundedIcon /> },
  { key: "organize_all", title: "Todo junto", helper: "Web + gestión + stock + cobros + turnos, adaptado a lo que necesite tu negocio.", icon: <AutoAwesomeRoundedIcon /> },
];
const GUIDED_NEED_KEYS = new Set(GUIDED_NEEDS.map((item) => item.key));

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function derivePrimaryNeed({ primaryNeed, wantsWebsite, wantsStock }) {
  if (GUIDED_NEED_KEYS.has(primaryNeed)) return primaryNeed;
  if (wantsWebsite === true && wantsStock === true) return "organize_all";
  if (wantsStock === true) return "stock";
  if (wantsWebsite === true) return "website";
  return "";
}

function intentFlags(primaryNeed) {
  if (primaryNeed === "website") return { wantsWebsite: true, wantsStock: false };
  if (primaryNeed === "stock") return { wantsWebsite: false, wantsStock: true };
  if (primaryNeed === "appointments") return { wantsWebsite: true, wantsStock: false };
  if (primaryNeed === "organize_all") return { wantsWebsite: true, wantsStock: true };
  return { wantsWebsite: false, wantsStock: false };
}

function guidedNeedLabel(value) {
  return GUIDED_NEEDS.find((item) => item.key === value)?.title || "Tu objetivo";
}

function localRecommendation(profile) {
  const businessGoals = [];
  const firstTasks = [];

  if (["stock", "organize_all"].includes(profile.primaryNeed) || profile.wantsStock === true) businessGoals.push("controlBusiness");
  if (profile.primaryNeed === "memberships") businessGoals.push("manageCustomers", "manageMemberships");
  if (profile.primaryNeed === "appointments") businessGoals.push("manageCustomers", "takeReservations");
  if (profile.primaryNeed === "property_payments") businessGoals.push("manageProperties", "manageLeases", "manageLeaseCollections");

  if (profile.wantsWebsite === true) firstTasks.push("publish_business_page");
  if (profile.wantsStock === true) firstTasks.push("initial_stock_count");
  if (profile.primaryNeed === "appointments") firstTasks.push("configure_agenda");
  if (profile.primaryNeed === "memberships") firstTasks.push("configure_business");
  if (profile.primaryNeed === "property_payments") firstTasks.push("create_first_property");
  if (!firstTasks.length) firstTasks.push("configure_business");

  return {
    routeKey: `general:${profile.primaryNeed || "start"}`,
    businessGoals: unique(businessGoals),
    capabilities: {},
    firstTasks: unique(firstTasks).slice(0, 3),
  };
}

function NeedCard({ option, selected, onSelect }) {
  return (
    <Paper
      component="button"
      type="button"
      onClick={() => onSelect(option.key)}
      elevation={0}
      sx={{
        width: "100%",
        p: { xs: 1.1, sm: 1.3 },
        minHeight: { xs: 96, sm: 108 },
        borderRadius: 3.2,
        border: selected ? "1px solid rgba(243,90,58,.72)" : "1px solid rgba(255,255,255,.08)",
        bgcolor: selected ? "rgba(243,90,58,.12)" : "rgba(255,255,255,.035)",
        color: "#fff",
        cursor: "pointer",
        textAlign: "left",
        font: "inherit",
        transition: "160ms ease",
        "&:hover": { borderColor: "rgba(243,90,58,.45)", bgcolor: selected ? "rgba(243,90,58,.14)" : "rgba(255,255,255,.055)" },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ width: 38, height: 38, borderRadius: 2.4, display: "grid", placeItems: "center", bgcolor: selected ? "rgba(243,90,58,.22)" : "rgba(243,90,58,.12)", color: "#FF9B82", flex: "0 0 auto" }}>
          {option.icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, lineHeight: 1.15, fontSize: { xs: ".9rem", sm: ".96rem" } }}>{option.title}</Typography>
          <Typography sx={{ mt: .38, color: BRAND.muted, fontSize: ".73rem", lineHeight: 1.35 }}>{option.helper}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function EmpecemosPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, register, loading: authLoading } = useAuth();
  const { createProject } = useProjects();
  const initialDraft = React.useMemo(() => loadAcquisitionDraft(), []);
  const isCurrentDraft = initialDraft.experienceVersion === ACQUISITION_EXPERIENCE_VERSION;
  const sessionId = React.useMemo(() => initialDraft.sessionId || acquisitionSessionId(), [initialDraft.sessionId]);
  const visitorId = React.useMemo(() => initialDraft.visitorId || acquisitionVisitorId(), [initialDraft.visitorId]);
  const referralCode = String(searchParams.get("ref") || initialDraft.referralCode || "").trim().toUpperCase();
  const entryPoint = String(searchParams.get("entry") || "start").trim();
  const initialStep = isCurrentDraft
    ? Math.max(0, Math.min(Number(initialDraft.stepIndex || 0), STEPS.length - 1))
    : 0;

  const initialWebsite = isCurrentDraft && typeof initialDraft.wantsWebsite === "boolean"
    ? initialDraft.wantsWebsite
    : isCurrentDraft && ["website", "organize_all"].includes(initialDraft.primaryNeed) ? true : null;
  const initialStock = isCurrentDraft && typeof initialDraft.wantsStock === "boolean"
    ? initialDraft.wantsStock
    : isCurrentDraft && ["stock", "organize_all"].includes(initialDraft.primaryNeed) ? true : null;
  const initialPrimaryNeed = isCurrentDraft && GUIDED_NEED_KEYS.has(initialDraft.primaryNeed)
    ? initialDraft.primaryNeed
    : derivePrimaryNeed({ wantsWebsite: initialWebsite, wantsStock: initialStock });
  const [stepIndex, setStepIndex] = React.useState(initialStep);
  const [profile, setProfile] = React.useState({
    businessProfile: isCurrentDraft ? initialDraft.businessProfile || "" : "",
    currentSystem: isCurrentDraft ? initialDraft.currentSystem || "" : "",
    itemVolumeBand: isCurrentDraft ? initialDraft.itemVolumeBand || "" : "",
    wantsWebsite: initialWebsite,
    wantsStock: initialStock,
    primaryNeed: initialPrimaryNeed,
    businessName: isCurrentDraft ? initialDraft.businessName || "" : "",
    locationText: isCurrentDraft ? initialDraft.locationText || "" : "",
  });
  const [contact, setContact] = React.useState({
    name: isCurrentDraft ? initialDraft.contactName || user?.name || "" : user?.name || "",
    email: isCurrentDraft ? initialDraft.contactEmail || user?.email || "" : user?.email || "",
    whatsapp: isCurrentDraft ? initialDraft.whatsapp || "" : "",
    password: "",
  });
  const [recommendation, setRecommendation] = React.useState(() => localRecommendation({ primaryNeed: initialPrimaryNeed, wantsWebsite: initialWebsite, wantsStock: initialStock }));
  const [error, setError] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const trackerRef = React.useRef(null);
  const initialTrackingRef = React.useRef(false);

  React.useEffect(() => {
    if (!user) return;
    setContact((current) => ({
      ...current,
      name: current.name || user.name || "",
      email: current.email || user.email || "",
    }));
  }, [user]);

  const stepKey = STEPS[stepIndex];
  const progressPct = Math.round((stepIndex / (STEPS.length - 1)) * 100);

  const eventPayload = React.useCallback((action, metadata = {}) => ({
    action,
    sessionId,
    visitorId,
    eventId: acquisitionEventId("ae"),
    metadata: {
      stepKey,
      stepIndex,
      totalSteps: STEPS.length,
      progressPct,
      entryPoint,
      experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
      audience: authLoading ? "unknown" : user ? "authenticated" : "anonymous",
      ...acquisitionDeviceMetadata(),
      ...metadata,
    },
  }), [authLoading, entryPoint, progressPct, sessionId, stepIndex, stepKey, user, visitorId]);

  React.useEffect(() => {
    if (authLoading || initialTrackingRef.current) return;
    initialTrackingRef.current = true;
    const campaign = acquisitionCampaign(searchParams);
    const audience = user ? "authenticated" : "anonymous";
    saveAcquisitionDraft({
      experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
      sessionId,
      visitorId,
      referralCode,
      stepIndex,
      ...profile,
    });
    acquisitionApi.startSession({
      sessionId,
      visitorId,
      entryPoint: "funnel",
      referralCode,
      campaign,
      audience,
      experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
    }).catch(() => null);
    acquisitionApi.track(eventPayload(initialStep > 0 ? "acquisition_funnel_resumed" : "acquisition_funnel_started", { direction: initialStep > 0 ? "resume" : "forward", audience })).catch(() => null);
    // Only once after auth resolution; dependencies intentionally captured at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  React.useEffect(() => {
    const tracker = {
      stepKey,
      startedAt: performance.now(),
      lastTickAt: performance.now(),
      lastInteractionAt: performance.now(),
      firstInteractionMs: null,
      activeMs: 0,
      idleMs: 0,
      hiddenMs: 0,
      flushed: false,
    };
    trackerRef.current = tracker;

    const interact = () => {
      const now = performance.now();
      tracker.lastInteractionAt = now;
      if (tracker.firstInteractionMs === null) tracker.firstInteractionMs = Math.max(0, Math.round(now - tracker.startedAt));
    };
    const tick = () => {
      const now = performance.now();
      const delta = Math.max(0, Math.min(2000, now - tracker.lastTickAt));
      tracker.lastTickAt = now;
      if (document.hidden) tracker.hiddenMs += delta;
      else if (now - tracker.lastInteractionAt > 12000) tracker.idleMs += delta;
      else tracker.activeMs += delta;
    };
    const interval = window.setInterval(tick, 750);
    window.addEventListener("pointerdown", interact, { passive: true });
    window.addEventListener("keydown", interact, { passive: true });
    window.addEventListener("input", interact, { passive: true });
    acquisitionApi.track(eventPayload("acquisition_step_viewed")).catch(() => null);

    return () => {
      tick();
      window.clearInterval(interval);
      window.removeEventListener("pointerdown", interact);
      window.removeEventListener("keydown", interact);
      window.removeEventListener("input", interact);
      if (!tracker.flushed) {
        tracker.flushed = true;
        acquisitionApi.trackKeepalive(eventPayload("acquisition_step_summary", {
          activeMs: Math.round(tracker.activeMs),
          idleMs: Math.round(tracker.idleMs),
          hiddenMs: Math.round(tracker.hiddenMs),
          firstInteractionMs: tracker.firstInteractionMs || 0,
        }));
      }
    };
  }, [eventPayload, stepKey]);

  React.useEffect(() => {
    const onPageHide = () => {
      const tracker = trackerRef.current;
      if (!tracker || tracker.flushed) return;
      tracker.flushed = true;
      acquisitionApi.trackKeepalive(eventPayload("acquisition_funnel_exited", { direction: "exit" }));
      acquisitionApi.trackKeepalive(eventPayload("acquisition_step_summary", {
        activeMs: Math.round(tracker.activeMs || 0),
        idleMs: Math.round(tracker.idleMs || 0),
        hiddenMs: Math.round(tracker.hiddenMs || 0),
        firstInteractionMs: tracker.firstInteractionMs || 0,
      }));
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [eventPayload]);

  const persistProfile = React.useCallback((nextProfile, nextStepIndex = stepIndex) => {
    const normalized = { ...nextProfile, primaryNeed: derivePrimaryNeed(nextProfile) };
    const nextRecommendation = localRecommendation(normalized);
    setProfile(normalized);
    setRecommendation(nextRecommendation);
    saveAcquisitionDraft({
      experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
      sessionId,
      visitorId,
      referralCode,
      stepIndex: nextStepIndex,
      ...normalized,
      recommendation: nextRecommendation,
    });
    acquisitionApi.updateSession(sessionId, {
      visitorId,
      audience: authLoading ? "unknown" : user ? "authenticated" : "anonymous",
      stepKey: STEPS[nextStepIndex] || stepKey,
      stepIndex: nextStepIndex,
      completionPct: Math.round((nextStepIndex / (STEPS.length - 1)) * 100),
      profile: normalized,
    }).then((data) => {
      if (data?.recommendation) setRecommendation(data.recommendation);
    }).catch(() => null);
    return normalized;
  }, [authLoading, referralCode, sessionId, stepIndex, stepKey, user, visitorId]);

  const goNext = React.useCallback(() => {
    setError("");
    setStepIndex((current) => {
      const next = Math.min(STEPS.length - 1, current + 1);
      saveAcquisitionDraft({ experienceVersion: ACQUISITION_EXPERIENCE_VERSION, stepIndex: next });
      return next;
    });
  }, []);

  const goBack = () => {
    if (stepIndex <= 0) {
      navigate("/");
      return;
    }
    acquisitionApi.track(eventPayload("acquisition_step_back", { direction: "back" })).catch(() => null);
    setStepIndex((current) => {
      const next = Math.max(0, current - 1);
      saveAcquisitionDraft({ experienceVersion: ACQUISITION_EXPERIENCE_VERSION, stepIndex: next });
      return next;
    });
  };

  const chooseGuidedNeed = (primaryNeed) => {
    const flags = intentFlags(primaryNeed);
    const next = persistProfile({ ...profile, primaryNeed, ...flags }, stepIndex);
    acquisitionApi.track(eventPayload("acquisition_answer_selected", { optionKey: `primaryNeed:${primaryNeed}` })).catch(() => null);
    setError("");
    return next;
  };

  const continueNeeds = () => {
    if (!GUIDED_NEED_KEYS.has(profile.primaryNeed)) {
      setError("Elegí la opción que más se parece a lo que querés resolver primero.");
      acquisitionApi.track(eventPayload("acquisition_step_validation_error", { reason: "service_intent" })).catch(() => null);
      return;
    }
    persistProfile(profile, stepIndex);
    acquisitionApi.track(eventPayload("acquisition_step_completed", { optionKey: `primaryNeed:${profile.primaryNeed}` })).catch(() => null);
    goNext();
  };

  const trackFormProgress = (formKey, fieldKey, values, requiredKeys) => {
    const requiredCompleted = requiredKeys.filter((key) => String(values?.[key] || "").trim()).length;
    acquisitionApi.track(eventPayload("acquisition_form_progress", {
      formKey,
      fieldKey,
      requiredCompleted,
      requiredTotal: requiredKeys.length,
      optionalCompleted: 0,
    })).catch(() => null);
  };

  const captureLead = async () => {
    const name = String(contact.name || "").trim();
    const businessName = String(profile.businessName || "").trim();
    const whatsapp = String(contact.whatsapp || "").trim();
    const email = String(contact.email || "").trim().toLowerCase();

    if (name.length < 2 || businessName.length < 2 || whatsapp.length < 6 || !email.includes("@")) {
      setError("Completá tu nombre, negocio, WhatsApp y un email válido.");
      acquisitionApi.track(eventPayload("acquisition_step_validation_error", { reason: "lead_identity" })).catch(() => null);
      return;
    }

    try {
      setCreating(true);
      setError("");
      const nextProfile = persistProfile({ ...profile, businessName }, stepIndex);
      saveAcquisitionDraft({
        experienceVersion: ACQUISITION_EXPERIENCE_VERSION,
        contactName: name,
        contactEmail: email,
        whatsapp,
        businessName,
      });

      // Conversion boundary: from this line forward the lead survives even if the
      // visitor abandons registration. This is the commercial source of truth.
      await acquisitionApi.updateSession(sessionId, {
        visitorId,
        audience: authLoading ? "unknown" : user ? "authenticated" : "anonymous",
        stepKey: "business_identity",
        stepIndex,
        completionPct: 72,
        profile: nextProfile,
        contact: { name, email, whatsapp },
      });
      await acquisitionApi.track(eventPayload("acquisition_contact_captured"));
      await acquisitionApi.track(eventPayload("acquisition_step_completed"));
      goNext();
    } catch (err) {
      setError(err?.message || "No pudimos guardar tus datos. Probá de nuevo.");
    } finally {
      setCreating(false);
    }
  };

  const createAccountAndProject = async () => {
    const name = String(contact.name || "").trim();
    const email = String(contact.email || "").trim().toLowerCase();
    if (!name || !email.includes("@")) {
      setError("Volvé al paso anterior y completá tus datos.");
      return;
    }
    if (!user && String(contact.password || "").length < 8) {
      setError("La contraseña necesita al menos 8 caracteres.");
      acquisitionApi.track(eventPayload("acquisition_step_validation_error", { reason: "password" })).catch(() => null);
      return;
    }

    try {
      setCreating(true);
      setError("");
      if (!user) {
        await acquisitionApi.track(eventPayload("acquisition_registration_started")).catch(() => null);
        await register({
          name,
          email,
          password: contact.password,
          referralCode: referralCode || undefined,
          prospectSessionId: sessionId,
        });
      }

      const seed = recommendation?.businessGoals ? recommendation : localRecommendation(profile);
      let createdProject = null;
      try {
        createdProject = await createProject({
          name: profile.businessName,
          businessProfile: PROFILE_KEYS.has(profile.businessProfile) ? profile.businessProfile : "",
          businessGoals: seed.businessGoals || [],
          capabilities: seed.capabilities || {},
          prospectSessionId: sessionId,
        });
      } catch (projectError) {
        // Account success remains success: project provisioning can recover in dashboard.
        console.error("[ACQUISITION PROJECT CREATE]", projectError);
      }

      clearAcquisitionDraft();
      if (createdProject?.slug) {
        navigate(`/dashboard/proyectos/${createdProject.slug}/inicio`, {
          replace: true,
          state: { onboardingMessage: "Tu negocio ya está creado. Ahora lo ponemos a funcionar paso a paso." },
        });
      } else {
        navigate("/dashboard", {
          replace: true,
          state: { onboardingMessage: "Tu cuenta está lista. Terminemos de crear tu negocio desde el panel." },
        });
      }
    } catch (err) {
      setError(err?.message || "No pudimos crear la cuenta. Tu lead ya quedó guardado para reintentar.");
    } finally {
      setCreating(false);
    }
  };

  React.useEffect(() => {
    if (stepKey === "business_identity") {
      acquisitionApi.track(eventPayload("acquisition_contact_started")).catch(() => null);
      acquisitionApi.track(eventPayload("acquisition_form_started", { formKey: "business_identity" })).catch(() => null);
    }
    if (stepKey === "contact") {
      acquisitionApi.track(eventPayload("acquisition_form_started", { formKey: "contact" })).catch(() => null);
    }
  }, [eventPayload, stepKey]);

  const titleByStep = {
    need: "¿Qué querés tener funcionando?",
    business_identity: "Perfecto. ¿Quién sos y cómo se llama tu negocio?",
    contact: user ? "Tu negocio está a un paso." : "Registrate gratis con Web Profit.",
  };
  const subtitleByStep = {
    need: "Elegí lo que más te gustaría resolver primero. Web Profit te permite usar todo junto: esto no limita nada dentro del sistema, sólo nos sirve para guiarte mejor.",
    business_identity: "Guardamos estos datos como lead antes del registro para que no pierdas lo que ya elegiste.",
    contact: user
      ? "Creamos tu espacio con lo que elegiste y entrás directo al panel."
      : "Obtené tu página web y tu sistema. En promedio, podés tener todo arriba y funcionando en unos 40 minutos.",
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: BRAND.dark, color: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ px: { xs: 1.2, sm: 2.2 }, py: 1.05, borderBottom: "1px solid rgba(255,255,255,.07)", bgcolor: "rgba(11,16,22,.92)", backdropFilter: "blur(16px)", zIndex: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={goBack} sx={{ color: "rgba(255,255,255,.72)" }}><ArrowBackRoundedIcon /></IconButton>
          <Typography sx={{ fontWeight: 950, letterSpacing: "-.03em" }}>Web Profit</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: ".76rem", fontWeight: 900, color: "rgba(255,255,255,.52)", minWidth: 42, textAlign: "right" }}>{stepIndex + 1}/{STEPS.length}</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={progressPct} sx={{ mt: .8, height: 4, borderRadius: 99, bgcolor: "rgba(255,255,255,.06)", "& .MuiLinearProgress-bar": { bgcolor: BRAND.orange, borderRadius: 99 } }} />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "grid", placeItems: "center", px: { xs: 1.2, sm: 2.4 }, py: { xs: 1.4, sm: 2.2 }, overflowY: "auto" }}>
        <Box sx={{ width: "100%", maxWidth: stepKey === "intro" ? 1080 : 760 }}>
          {stepKey !== "intro" ? (
            <Stack spacing={.65} sx={{ mb: 1.3, textAlign: "left" }}>
              <Typography component="h1" sx={{ maxWidth: 730, fontWeight: 950, letterSpacing: "-.045em", lineHeight: 1.02, fontSize: { xs: "2rem", sm: "2.7rem", md: "3.2rem" } }}>{titleByStep[stepKey]}</Typography>
              <Typography sx={{ color: BRAND.muted, maxWidth: 700, fontSize: { xs: ".88rem", sm: ".98rem" }, lineHeight: 1.45 }}>{subtitleByStep[stepKey]}</Typography>
            </Stack>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              p: stepKey === "intro" ? { xs: 1.6, sm: 2.4, md: 3 } : { xs: 1.35, sm: 1.8 },
              borderRadius: { xs: 3.4, sm: 4.2 },
              color: "#fff",
              bgcolor: BRAND.panel,
              border: "1px solid rgba(255,255,255,.075)",
              boxShadow: "0 26px 80px rgba(0,0,0,.28)",
            }}
          >
            {stepKey === "intro" ? (
              <Stack spacing={{ xs: 1.5, sm: 2 }}>
                <Stack direction="row" spacing={.75} flexWrap="wrap" useFlexGap>
                  <Chip icon={<AutoAwesomeRoundedIcon />} label="Empezá gratis" sx={{ color: "#fff", bgcolor: "rgba(243,90,58,.15)", fontWeight: 900 }} />
                  <Chip label="Sin preguntas técnicas" variant="outlined" sx={{ color: "rgba(255,255,255,.75)", borderColor: "rgba(255,255,255,.12)", fontWeight: 850 }} />
                </Stack>
                <Box>
                  <Typography component="h1" sx={{ maxWidth: 850, fontWeight: 950, letterSpacing: "-.055em", lineHeight: .98, fontSize: { xs: "2.45rem", sm: "3.7rem", md: "4.7rem" } }}>
                    Tu negocio online, sin convertirte en programador.
                  </Typography>
                  <Typography sx={{ mt: 1.1, maxWidth: 760, color: "rgba(255,255,255,.64)", fontSize: { xs: ".92rem", sm: "1.08rem" }, lineHeight: 1.5 }}>
                    Decinos qué querés tener funcionando primero. Puede ser web, stock, mensualidades, turnos, inmuebles o todo junto. Después dejás tus datos y recién ahí elegís si crear tu cuenta gratis.
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    onClick={() => {
                      acquisitionApi.track(eventPayload("acquisition_cta_clicked", { optionKey: "start_short_flow" })).catch(() => null);
                      acquisitionApi.track(eventPayload("acquisition_step_completed", { optionKey: "intro_continue" })).catch(() => null);
                      goNext();
                    }}
                    variant="contained"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{ minHeight: 54, px: 2.35, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, boxShadow: "0 16px 34px rgba(243,90,58,.28)", "&:hover": { bgcolor: "#E64A2A" } }}
                  >
                    Empezar ahora
                  </Button>
                  <Typography sx={{ alignSelf: "center", color: "rgba(255,255,255,.45)", fontSize: ".76rem" }}>
                    El lead queda guardado antes del registro.
                  </Typography>
                </Stack>
              </Stack>
            ) : null}

            {stepKey === "need" ? (
              <Stack spacing={1.05}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: .85 }}>
                  {GUIDED_NEEDS.map((option) => (
                    <NeedCard
                      key={option.key}
                      option={option}
                      selected={profile.primaryNeed === option.key}
                      onSelect={chooseGuidedNeed}
                    />
                  ))}
                </Box>
                <Paper elevation={0} sx={{ p: 1.05, borderRadius: 2.8, bgcolor: "rgba(255,255,255,.035)", border: "1px solid rgba(255,255,255,.07)" }}>
                  <Typography sx={{ color: "rgba(255,255,255,.68)", fontSize: ".75rem", lineHeight: 1.4 }}>
                    <Box component="span" sx={{ color: "#fff", fontWeight: 950 }}>No te encierra en un plan.</Box>{" "}
                    Web Profit puede combinar todas estas herramientas. Esta respuesta sólo nos ayuda a entender qué necesitás primero y a ordenar mejor tu inicio.
                  </Typography>
                </Paper>
                {error ? <Alert severity="warning">{error}</Alert> : null}
                <Button onClick={continueNeeds} variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ minHeight: 50, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#E64A2A" } }}>
                  Continuar
                </Button>
              </Stack>
            ) : null}

            {stepKey === "business_identity" ? (
              <Stack spacing={1.05}>
                <Stack direction="row" spacing={.65} flexWrap="wrap" useFlexGap sx={{ mb: .15 }}>
                  <Chip size="small" label={`Querés: ${guidedNeedLabel(profile.primaryNeed)}`} sx={{ fontWeight: 850 }} />
                </Stack>
                <TextField autoFocus fullWidth label="Tu nombre" value={contact.name} onChange={(event) => setContact((current) => ({ ...current, name: event.target.value }))} onBlur={() => trackFormProgress("business_identity", "name", { ...contact, businessName: profile.businessName }, ["name", "businessName", "whatsapp", "email"])} disabled={creating || Boolean(user)} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                <TextField fullWidth label="Nombre de tu negocio" value={profile.businessName} onChange={(event) => setProfile((current) => ({ ...current, businessName: event.target.value }))} onBlur={() => trackFormProgress("business_identity", "businessName", { ...contact, businessName: profile.businessName }, ["name", "businessName", "whatsapp", "email"])} disabled={creating} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                <TextField fullWidth label="WhatsApp" placeholder="Ej: +54 9 260..." value={contact.whatsapp} onChange={(event) => setContact((current) => ({ ...current, whatsapp: event.target.value }))} onBlur={() => trackFormProgress("business_identity", "whatsapp", { ...contact, businessName: profile.businessName }, ["name", "businessName", "whatsapp", "email"])} disabled={creating} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                <TextField fullWidth type="email" label="Email" value={contact.email} onChange={(event) => setContact((current) => ({ ...current, email: event.target.value }))} onBlur={() => trackFormProgress("business_identity", "email", { ...contact, businessName: profile.businessName }, ["name", "businessName", "whatsapp", "email"])} disabled={creating || Boolean(user)} sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 } }} />
                <Typography sx={{ color: "rgba(255,255,255,.45)", fontSize: ".7rem", lineHeight: 1.35, textAlign: "left" }}>
                  Al continuar guardamos este contacto como lead de Web Profit. Todavía no necesitás crear una cuenta.
                </Typography>
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Button onClick={captureLead} disabled={creating} variant="contained" startIcon={creating ? <WebProfitSpinner size={17} /> : undefined} endIcon={!creating ? <ArrowForwardRoundedIcon /> : undefined} sx={{ minHeight: 52, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#E64A2A" } }}>
                  {creating ? "Guardando..." : "Guardar mis datos y seguir"}
                </Button>
              </Stack>
            ) : null}

            {stepKey === "contact" ? (
              <Stack spacing={1.05}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.15, borderRadius: 2.8, bgcolor: "rgba(255,255,255,.035)" }}>
                  <StorefrontRoundedIcon sx={{ color: "#FFB19A" }} />
                  <Box sx={{ textAlign: "left", minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950 }}>{profile.businessName || "Tu negocio"}</Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: ".76rem" }}>{contact.name} · {contact.email} · {contact.whatsapp}</Typography>
                  </Box>
                </Stack>
                {!user ? (
                  <TextField
                    autoFocus
                    fullWidth
                    type="password"
                    label="Creá una contraseña"
                    helperText="Mínimo 8 caracteres"
                    value={contact.password}
                    onChange={(event) => setContact((current) => ({ ...current, password: event.target.value }))}
                    onBlur={() => trackFormProgress("contact", "password", contact, ["password"])}
                    disabled={creating}
                    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 2.7 }, "& .MuiFormHelperText-root": { color: "rgba(255,255,255,.48)" } }}
                  />
                ) : null}
                <Paper elevation={0} sx={{ p: 1.2, borderRadius: 2.8, bgcolor: "rgba(243,90,58,.09)", border: "1px solid rgba(243,90,58,.16)" }}>
                  <Typography sx={{ fontWeight: 950 }}>En unos 40 minutos podés tener tu base funcionando.</Typography>
                  <Typography sx={{ mt: .3, color: BRAND.muted, fontSize: ".78rem", lineHeight: 1.4 }}>
                    Web editable, panel y las herramientas que elegiste. Después seguís mejorándolo a tu ritmo.
                  </Typography>
                </Paper>
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Button onClick={createAccountAndProject} disabled={creating} variant="contained" startIcon={creating ? <WebProfitSpinner size={17} /> : undefined} endIcon={!creating ? <ArrowForwardRoundedIcon /> : undefined} sx={{ minHeight: 52, borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#E64A2A" } }}>
                  {creating ? "Preparando Web Profit..." : user ? "Crear mi negocio" : "Registrarme gratis y empezar"}
                </Button>
              </Stack>
            ) : null}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
